import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import Badge from 'components/base/Badge';

export interface AssignmentToggleOption {
  id: number;
  label: string;
  detail?: string | null;
  badge?: string | null;
  checked?: boolean;
  disabled?: boolean;
}

const AssignmentToggleList = ({
  options,
  selectedIds,
  onToggle,
  searchPlaceholder,
  assignedSummary,
  emptyMessage,
  checkboxPrefix,
}: {
  options: AssignmentToggleOption[];
  selectedIds: number[];
  onToggle: (id: number) => void;
  searchPlaceholder: string;
  assignedSummary: string;
  emptyMessage: string;
  checkboxPrefix: string;
}) => {
  const [search, setSearch] = useState('');
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const normalizedSearch = search.trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) =>
      [option.label, option.detail, option.badge].filter(Boolean).some((value) => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [normalizedSearch, options]);

  return (
    <div>
      <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center mb-3">
        <Form.Control type="search" value={search} placeholder={searchPlaceholder} onChange={(event) => setSearch(event.target.value)} />
        <div className="text-muted text-nowrap">{assignedSummary}</div>
      </div>

      <div className="border rounded" style={{ maxHeight: '420px', overflowY: 'auto' }}>
        {filteredOptions.length ? (
          filteredOptions.map((option) => {
            const checkboxId = `${checkboxPrefix}-${option.id}`;
            const checked = option.checked ?? selectedIdSet.has(option.id);

            return (
              <div key={option.id} className="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                <label
                  htmlFor={checkboxId}
                  className="mb-0 flex-grow-1"
                  style={{
                    cursor: option.disabled ? 'default' : 'pointer',
                    minWidth: 0,
                  }}
                >
                  <span className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-break">{option.label}</span>
                    {option.badge ? (
                      <Badge variant="phoenix" bg="info">
                        {option.badge}
                      </Badge>
                    ) : null}
                  </span>
                  {option.detail ? <small className="d-block text-muted text-break">{option.detail}</small> : null}
                </label>
                <Form.Check id={checkboxId} type="checkbox" checked={checked} disabled={option.disabled} onChange={() => onToggle(option.id)} />
              </div>
            );
          })
        ) : (
          <p className="text-muted m-3">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
};

export default AssignmentToggleList;
